/*
 * Pantora Bridal: gowns in Horizon predictive search.
 *
 * Standalone. Horizon's predictive-search files are not modified; this
 * script only adds a "Gowns" group inside the dropdown Horizon renders.
 *
 * - Shopify predictive search does not index metaobjects, so gowns come from
 *   a small JSON index rendered by sections/pantora-gown-index (through
 *   snippets/pantora-gowns). v1 matching: name, season, explicit search
 *   keywords, structured attributes. No descriptions, no prices.
 * - Lazy: the index is requested on first focus/typing in a search field,
 *   once per page, then kept in sessionStorage for 10 minutes.
 * - Horizon re-renders (morphs) the dropdown on every keystroke. A
 *   MutationObserver re-adds the group after each refresh; the group carries
 *   the query it was built for, so it is never duplicated.
 * - Failure is silent for shoppers: if the index cannot load or anything
 *   here throws, a console warning is logged and Horizon's own predictive
 *   search keeps working untouched.
 */
(() => {
  if (window.__pantoraGownPredictive) return;
  window.__pantoraGownPredictive = true;

  const script = document.currentScript;
  const DEFAULT_SECTION = (script && script.dataset.indexSection) || 'pantora-gown-index';
  const TTL_MS = 10 * 60 * 1000;
  const MAX_ITEMS = 4;
  const GROUP_CLASS = 'p-pred-gowns';

  const state = { idx: null, loading: null, failed: false, source: null, bytes: 0, injections: 0 };
  window.PantoraGownSearch = { state };

  // QA hook: a draft-only template may point the index at a disposable section.
  const sectionId = () => document.documentElement.dataset.pantoraGownIndex || DEFAULT_SECTION;

  const norm = (s) =>
    String(s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const prep = (list) =>
    (Array.isArray(list) ? list : [])
      .filter((g) => g && g.n && g.u)
      .map((g) => ({ ...g, _n: ' ' + norm(g.n), _h: ' ' + norm([g.n, g.s, g.k, g.a].join(' ')) }));

  function readCache(key) {
    try {
      const raw = window.sessionStorage.getItem(key);
      if (!raw) return null;
      const c = JSON.parse(raw);
      if (!c || Date.now() - c.t > TTL_MS || !Array.isArray(c.d)) return null;
      return c.d;
    } catch (e) {
      return null;
    }
  }

  function writeCache(key, data) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), d: data }));
    } catch (e) {
      /* storage unavailable: in-memory only */
    }
  }

  function load() {
    if (state.idx) return Promise.resolve(state.idx);
    if (state.failed) return Promise.resolve(null);
    if (state.loading) return state.loading;

    const sid = sectionId();
    const key = 'pantora-gowns:v1:' + sid;
    const cached = readCache(key);
    if (cached) {
      state.idx = prep(cached);
      state.source = 'cache';
      return Promise.resolve(state.idx);
    }

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = new URL(root, window.location.origin);
    url.searchParams.set('section_id', sid);

    state.loading = fetch(url.toString(), { credentials: 'same-origin' })
      .then((r) => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const el = doc.querySelector('script[data-pantora-gown-index]');
        if (!el) throw new Error('index not found in section response');
        if (doc.querySelector('[data-pantora-gown-index-incomplete]')) {
          console.warn('[pantora-gowns] gown index spans more than one page; predictive results are incomplete');
        }
        const data = JSON.parse(el.textContent);
        writeCache(key, data);
        state.idx = prep(data);
        state.source = 'network';
        state.bytes = text.length;
        return state.idx;
      })
      .catch((e) => {
        console.warn('[pantora-gowns] gown index unavailable; native predictive search unaffected', e);
        state.failed = true;
        return null;
      })
      .finally(() => {
        state.loading = null;
      });
    return state.loading;
  }

  function match(q) {
    const toks = norm(q).split(' ').filter(Boolean);
    if (!toks.length || !state.idx) return { total: 0, items: [] };
    const res = [];
    for (const g of state.idx) {
      if (toks.every((t) => g._h.includes(' ' + t))) {
        res.push([toks.every((t) => g._n.includes(' ' + t)) ? 0 : 1, g]);
      }
    }
    res.sort((a, b) => a[0] - b[0] || a[1].n.localeCompare(b[1].n));
    return { total: res.length, items: res.slice(0, MAX_ITEMS).map((r) => r[1]) };
  }

  const esc = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  function inject(comp) {
    const input = comp.querySelector('input[type="search"], [ref="searchInput"]');
    const inner = comp.querySelector('.predictive-search-results__inner');
    if (!input || !inner) return;

    const q = input.value.trim();
    const existing = inner.querySelectorAll('.' + GROUP_CLASS);
    if (existing.length === 1 && existing[0].dataset.q === q) return;
    existing.forEach((e) => e.remove());
    if (!q || !state.idx) return;

    const { total, items } = match(q);
    const wrap = document.createElement('div');
    wrap.id = 'pantora-predictive-gowns';
    wrap.className = GROUP_CLASS;
    wrap.dataset.q = q;

    if (!total) {
      wrap.hidden = true;
      inner.append(wrap);
      return;
    }

    wrap.innerHTML =
      '<h4 class="predictive-search-results__title">Gowns</h4>' +
      '<ul class="predictive-search-results__list list-unstyled" role="list">' +
      items
        .map(
          (g) =>
            '<li class="p-pred-gown" ref="resultsItems[]"><a href="' + esc(g.u) + '">' +
            (g.i
              ? '<img src="' + esc(g.i) + '" alt="" width="40" height="53" loading="lazy">'
              : '<span class="p-pred-thumb" aria-hidden="true"></span>') +
            '<span class="p-pred-text"><span class="p-pred-name">' + esc(g.n) + '</span>' +
            (g.s ? '<span class="p-pred-season">' + esc(g.s) + '</span>' : '') +
            '</span></a></li>'
        )
        .join('') +
      '</ul>';

    const status = inner.querySelector('[role="status"]');
    if (status) status.after(wrap);
    else inner.prepend(wrap);

    const nativeItems = [...inner.querySelectorAll('.predictive-search-results__card, [ref="resultsItems[]"]')].filter(
      (e) => !e.closest('.' + GROUP_CLASS) && !e.classList.contains('predictive-search-results__card--query')
    ).length;

    const noRes = inner.querySelector('.predictive-search-results__no-results');
    if (noRes) noRes.hidden = true;

    if (status) {
      const label = total + ' bridal ' + (total === 1 ? 'gown' : 'gowns');
      status.textContent =
        noRes || !nativeItems ? label + ' found for “' + q + '”.' : status.textContent.trim() + ' Plus ' + label + '.';
    }

    const single = inner.querySelector('[data-single-result-url]');
    if (total === 1 && !nativeItems) {
      const d = single || inner.appendChild(document.createElement('div'));
      d.setAttribute('data-single-result-url', items[0].u);
    } else if (single) {
      // A native single result plus gowns is no longer a single result.
      single.remove();
    }
    state.injections++;
  }

  function attach(comp) {
    if (comp.__pantoraGowns) return;
    comp.__pantoraGowns = true;

    let queued = false;
    const run = () => {
      queued = false;
      try {
        inject(comp);
      } catch (e) {
        console.warn('[pantora-gowns] could not add gowns to predictive search', e);
      }
    };
    const schedule = () => {
      if (queued) return;
      queued = true;
      window.setTimeout(run, 0);
    };
    const warm = () => load().then((idx) => idx && schedule());

    const input = comp.querySelector('input[type="search"], [ref="searchInput"]');
    if (input) {
      input.addEventListener('focus', warm, { once: true });
      input.addEventListener('input', warm);
    }

    new MutationObserver(() => {
      if (state.idx) schedule();
    }).observe(comp, { childList: true, subtree: true });
  }

  function addStyles() {
    if (document.getElementById('pantora-pred-gowns-css')) return;
    const css = document.createElement('style');
    css.id = 'pantora-pred-gowns-css';
    css.textContent =
      '.p-pred-gowns{margin-block-end:var(--padding-lg,16px)}' +
      '.p-pred-gowns .p-pred-gown a{display:flex;gap:12px;align-items:center;text-decoration:none;color:inherit;padding:6px 0}' +
      '.p-pred-gowns .p-pred-gown img,.p-pred-gowns .p-pred-thumb{width:40px;height:53px;object-fit:cover;object-position:center top;flex:none;background:rgb(var(--color-foreground-rgb,0 0 0) / 0.06)}' +
      '.p-pred-gowns .p-pred-text{display:grid;gap:2px}' +
      '.p-pred-gowns .p-pred-season{font-size:.7rem;letter-spacing:.1em;text-transform:uppercase;opacity:.65}' +
      '.p-pred-gowns .p-pred-gown a:hover .p-pred-name{text-decoration:underline;text-underline-offset:3px}' +
      '.p-pred-gowns .keyboard-focus a,.p-pred-gowns [aria-selected="true"] a{outline:2px solid currentColor;outline-offset:2px}';
    document.head.appendChild(css);
  }

  function scan() {
    try {
      const comps = document.querySelectorAll('predictive-search-component');
      if (comps.length) addStyles();
      comps.forEach(attach);
    } catch (e) {
      console.warn('[pantora-gowns] predictive search extension disabled', e);
    }
  }

  scan();
  document.addEventListener('DOMContentLoaded', scan);
  document.addEventListener('shopify:section:load', scan);
})();
